import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
} from "reactstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addClientPlan,
  getClientPlanDataById,
  updateClientPlan,
} from "model/ClientPlanModel";

const digitsOnly = (value) => String(value ?? "").replace(/[^0-9]/g, "");

/* -------------------- Yup Schema -------------------- */
const clientPlanSchema = Yup.object().shape({
  title: Yup.string().trim().required("Title is required"),
  incentiveMin: Yup.string()
    .trim()
    .required("Incentive Min is required")
    .matches(/^\d+$/, "Incentive Min must be a number"),
  incentiveMax: Yup.string()
    .trim()
    .required("Incentive Max is required")
    .matches(/^\d+$/, "Incentive Max must be a number"),
  status: Yup.boolean(),
});

const ClientPlanAddEditModal = ({
  setClientPlanAddEditModalOpen,
  clientPlanAddEditModalOpen,
  getClientPlanList,
  itemId,
  setFormData,
  formData,
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});

  const closeModal = () => {
    setClientPlanAddEditModalOpen(false);
    setErrors({});
  };

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    let fieldValue = type === "checkbox" || type === "switch" ? checked : value;

    if (name === "incentiveMin" || name === "incentiveMax") {
      fieldValue = digitsOnly(value);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));

    if (!clientPlanSchema.fields[name]) return;

    try {
      await Yup.reach(clientPlanSchema, name).validate(fieldValue);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await clientPlanSchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const params = {
      title: formData?.title || "",
      incentiveMin: Number(formData?.incentiveMin) || 0,
      incentiveMax: Number(formData?.incentiveMax) || 0,
      status: formData?.status ? "active" : "deactive",
    };

    let response;
    if (itemId) {
      response = await updateClientPlan(itemId, params);
    } else {
      response = await addClientPlan(params);
    }

    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getClientPlanList();
    } else {
      toast.error(response?.message);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchClientPlanById = async (id) => {
    const response = await getClientPlanDataById({ Id: id });
    if (response?.data) {
      setFormData({
        title: response?.data?.title || "",
        incentiveMin:
          response?.data?.incentiveMin != null
            ? String(response?.data?.incentiveMin)
            : "",
        incentiveMax:
          response?.data?.incentiveMax != null
            ? String(response?.data?.incentiveMax)
            : "",
        status:
          response?.data?.status === "active" ||
          response?.data?.status === true,
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchClientPlanById(itemId);
    }
  }, [itemId]);

  return (
    <Modal
      isOpen={clientPlanAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">
            {itemId ? "Update" : "Create"} Client Plan
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>
              Title <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="title"
              value={formData?.title ?? ""}
              onChange={handleChange}
            />
            {errors.title && (
              <small className="text-danger">{errors.title}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Incentive Min <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="incentiveMin"
              inputMode="numeric"
              value={formData?.incentiveMin ?? ""}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {errors.incentiveMin && (
              <small className="text-danger">{errors.incentiveMin}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Incentive Max <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="incentiveMax"
              inputMode="numeric"
              value={formData?.incentiveMax ?? ""}
              onChange={handleChange}
              onKeyDown={(e) => {
                if (["e", "E", "+", "-", ".", ","].includes(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {errors.incentiveMax && (
              <small className="text-danger">{errors.incentiveMax}</small>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              Status <span className="text-danger">*</span>
            </Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "active" : "deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    key={`status-${formKey}-${formData?.status}`}
                    type="switch"
                    name="status"
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              {itemId ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ClientPlanAddEditModal;
